import React, { useEffect, useState } from 'react';
import { Archive, History, StickyNote, Trash2, Loader2, RefreshCw, ShieldCheck, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

/**
 * Shared admin panel exposing:
 *  - "Archives" section (super_admin + managers only)
 *  - Per-report history & note trail (any admin)
 *
 * Props:
 *  - kind: 'content' | 'group' | 'shop' | 'mymouv'
 *  - selectedReportId?: string      (opens a history/note modal for that report)
 *  - onCloseSelected?: () => void
 *  - onDeleted?: (id) => void        (called after archive delete)
 *  - variant?: 'archive' | 'history' (default: both)
 */
const AdminReportsAuditPanel = ({ kind, selectedReportId, onCloseSelected, onDeleted }) => {
  const { user } = useAuth();
  const isManagerOrSuper = (() => {
    const role = String(user?.admin_role || '').toLowerCase();
    const email = String(user?.email || '').toLowerCase();
    return (
      user?.is_super_admin ||
      role === 'super_admin' ||
      role === 'manager' ||
      email === 'contact.destinestudio@gmail.com'
    );
  })();

  const [showArchive, setShowArchive] = useState(false);
  const [archiveItems, setArchiveItems] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [note, setNote] = useState('');
  const [postingNote, setPostingNote] = useState(false);

  const loadArchive = async () => {
    setArchiveLoading(true);
    try {
      const res = await axios.get(`/api/admin/reports-audit/archive/${kind}`, { params: { limit: 200 } });
      setArchiveItems(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement archives');
    } finally {
      setArchiveLoading(false);
    }
  };

  useEffect(() => { if (showArchive) loadArchive(); }, [showArchive, kind]);

  const loadHistory = async () => {
    if (!selectedReportId) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/api/admin/reports-audit/${kind}/${selectedReportId}/history`);
      setHistory(res.data.history || []);
    } catch (e) {
      toast.error('Erreur chargement historique');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, [selectedReportId, kind]);

  const submitNote = async (e) => {
    e.preventDefault();
    if (!selectedReportId) return;
    const clean = note.trim();
    if (clean.length < 3) return toast.error('Note trop courte (min 3 caractères)');
    setPostingNote(true);
    try {
      await axios.post(`/api/admin/reports-audit/${kind}/${selectedReportId}/note`, { note: clean });
      setNote('');
      toast.success('Note ajoutée');
      loadHistory();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Erreur');
    } finally {
      setPostingNote(false);
    }
  };

  const deleteArchived = async (id) => {
    if (!window.confirm('Supprimer définitivement ce signalement archivé ?')) return;
    try {
      await axios.delete(`/api/admin/reports-audit/archive/${kind}/${id}`);
      setArchiveItems((prev) => prev.filter((it) => it.id !== id));
      toast.success('Signalement supprimé');
      onDeleted?.(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <>
      {/* Archive toggle (visible only to super admins + managers) */}
      {isManagerOrSuper && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowArchive(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-sm font-medium hover:bg-amber-500/20"
            data-testid={`open-archive-${kind}`}
          >
            <Archive className="w-4 h-4" />
            Archives (Manager+)
            <ShieldCheck className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Archive modal */}
      {showArchive && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4" data-testid={`archive-modal-${kind}`}>
          <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-3xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-amber-400" />
                <h2 className="text-xl font-bold">Archives — {kind}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={loadArchive} className="p-2 hover:bg-white/10 rounded-full" title="Rafraîchir">
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={() => setShowArchive(false)} className="p-2 hover:bg-white/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {archiveLoading ? (
              <div className="flex items-center gap-2 text-gray-400 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
            ) : archiveItems.length === 0 ? (
              <p className="text-sm text-gray-400 py-6">Aucun signalement archivé.</p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {archiveItems.map((it) => (
                  <div key={it.id} className="p-3 rounded-xl bg-white/5 border border-white/10" data-testid={`archive-item-${it.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-400 mb-1">
                          {(it.reviewed_at || it.resolved_at || it.updated_at || '').slice(0, 19).replace('T', ' ')}
                          {' · '}
                          <span className="uppercase text-amber-300">{it.status}</span>
                        </div>
                        <div className="text-sm font-medium">{it.reason || it.content || it.reasons?.[0] || '(sans motif)'}</div>
                        {it.action_taken && <div className="text-xs text-emerald-300 mt-1">Action: {it.action_taken}</div>}
                        {Array.isArray(it.history) && it.history.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-cyan-300 cursor-pointer">Historique ({it.history.length})</summary>
                            <ul className="mt-1 space-y-1">
                              {it.history.map((h, idx) => (
                                <li key={idx} className="text-xs text-gray-300 pl-3 border-l border-white/10">
                                  <strong>{h.by_name || h.by_id}</strong> · {h.action} · <span className="text-gray-500">{(h.at || '').slice(0, 19).replace('T', ' ')}</span>
                                  <div className="text-gray-400">{h.note}</div>
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteArchived(it.id)}
                        className="p-2 rounded-full hover:bg-red-500/10 text-red-400 flex-shrink-0"
                        title="Supprimer définitivement"
                        data-testid={`delete-archive-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History + note modal for a specific report */}
      {selectedReportId && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4" data-testid={`history-modal-${kind}`}>
          <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-cyan-400" />
                <h2 className="text-xl font-bold">Historique du signalement</h2>
              </div>
              <button onClick={onCloseSelected} className="p-2 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[40vh] overflow-y-auto space-y-2 mb-4">
              {historyLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-400">Aucune note ajoutée pour l&apos;instant.</p>
              ) : (
                history.map((h, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-white/5 border border-white/10">
                    <div className="text-xs text-gray-400">
                      <strong className="text-white">{h.by_name || h.by_id}</strong> · {h.action} · {(h.at || '').slice(0, 19).replace('T', ' ')}
                    </div>
                    <div className="text-sm text-gray-200 mt-1">{h.note}</div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={submitNote} className="space-y-2">
              <label className="text-xs font-medium text-gray-400 flex items-center gap-1">
                <StickyNote className="w-3.5 h-3.5" /> Ajouter une note (obligatoire lors du traitement)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500 text-sm"
                placeholder="Ex: Contenu supprimé, utilisateur averti par email…"
                data-testid={`note-input-${kind}`}
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="submit"
                  disabled={postingNote}
                  className="px-4 py-2 rounded-full bg-gradient-to-r from-magenta-500 to-violet-600 text-white font-semibold text-sm disabled:opacity-50"
                  data-testid={`submit-note-${kind}`}
                >
                  {postingNote ? 'Envoi…' : 'Enregistrer la note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminReportsAuditPanel;
